import styled from 'styled-components';
import colors from '@honeybook/hbui/dist/styles/colors';
import Web16 from '@honeybook/icons/dist/icons/Web16';
import React from 'react';
import { observer } from 'mobx-react';
import { useRootStore } from '~/stores/useRootStore';
import { IFlowTemplate } from '~/types/model.types';
import { useTranslation } from 'react-i18next';
import { NAMESPACES } from '~/i18n/namespaces';
import { useModal } from '~/providers/modal.provider';
import PublicTemplateConfigurationModalContainer from '~/components/Templates/components/FlowTemplates/PublicTemplateConfigurationModalContainer';
import useAnalytics from '~/common/useAnalytics';
import { getClickShareTemplateSource } from '~/components/Templates/templates.utils';
import HBTooltip from '@honeybook/hbui/dist/components/HBTooltip/HBTooltip';
import { Placement } from '@popperjs/core';

interface Props {
    flow: IFlowTemplate;
    className?: string;
    placement?: Placement;
}

const PublishedTemplateIcon = ({
    flow,
    className,
    placement = 'right'
}: Props) => {
    const {
        templatesUiStore: { updateSelectedTemplate }
    } = useRootStore();
    const { t } = useTranslation(NAMESPACES.TEMPLATES, {
        keyPrefix: 'share_template.publish_template'
    });
    const { openModal } = useModal();
    const { track } = useAnalytics();

    if (!flow?.public_configuration?.published_template) {
        return null;
    }

    const onClick = e => {
        track('public sharing globe icon', {
            action: 'click',
            source: getClickShareTemplateSource(),
            template_id: flow._id
        });
        updateSelectedTemplate(flow);
        openModal('PublicTemplateConfiguration', {
            component: <PublicTemplateConfigurationModalContainer />
        });
        e.stopPropagation();
    };

    return (
        <IconTooltip className={className}>
            <HBTooltip
                placement={placement}
                customStyle={{
                    width: '155px'
                }}
                target={
                    <Container onClick={onClick} className={className}>
                        <Icon color={colors.black} />
                    </Container>
                }
            >
                {t('published_template_icon_tooltip')}
            </HBTooltip>
        </IconTooltip>
    );
};

export default observer(PublishedTemplateIcon);

const Container = styled.div`
    background-color: ${colors.white};
    height: 30px;
    width: 30px;
    border-radius: 3px;
    justify-content: center;
    display: flex;
    cursor: pointer;
`;

const Icon = styled(Web16)`
    align-self: center;
    padding-top: 3px;
`;

export const IconTooltip = styled.div`
    position: absolute;
    height: 30px;
    width: 30px;
    justify-content: center;
    margin: 8px;
    right: 0;
`;
